import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CatalogItem {
  id: string;
  name: string;
  description: string | null;
  base_cost: number;
  unit_symbol: string | null;
  browse_category_id: string | null;
  item_type: 'internal' | 'assembly' | 'system';
  component_type: string;
  image_urls: string[];
  markup_percentage: number;
  final_price: number;
  trade_id: string | null;
  category_id: string | null;
  total_count: number;
}

export interface CatalogFilters {
  search: string;
  browse_category_id: string | null;
  item_types: string[] | null;
  trade_id: string | null;
  classification_category_id: string | null;
  include_system: boolean;
}

const DEFAULT_FILTERS: CatalogFilters = {
  search: '',
  browse_category_id: null,
  item_types: null,
  trade_id: null,
  classification_category_id: null,
  include_system: true,
};

const PAGE_SIZE = 24;
const DEBOUNCE_MS = 250;

export function useCatalogSearch(companyId: string | null) {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<CatalogFilters>(DEFAULT_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const updateSearch = useCallback((value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, DEBOUNCE_MS);
  }, []);

  const vatQuery = useQuery({
    queryKey: ['company_vat', companyId],
    queryFn: async () => {
      if (!companyId) return { is_vat_registered: false, vat_rate: 15 };
      const { data, error } = await supabase
        .from('companies')
        .select('is_vat_registered, "VAT Rate"')
        .eq('id', companyId)
        .single();
      if (error || !data) return { is_vat_registered: false, vat_rate: 15 };
      return {
        is_vat_registered: data.is_vat_registered ?? false,
        vat_rate: (data as any)['VAT Rate'] ?? 15,
      };
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });

  const queryKey = [
    'catalog_search',
    companyId,
    debouncedSearch,
    filters.browse_category_id,
    filters.item_types,
    filters.trade_id,
    filters.classification_category_id,
    filters.include_system,
    page,
    vatQuery.data?.is_vat_registered,
    vatQuery.data?.vat_rate,
  ];

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<CatalogItem[]> => {
      if (!companyId) return [];
      const { data, error } = await (supabase.rpc as any)('search_catalog', {
        p_company_id: companyId,
        p_search: debouncedSearch || null,
        p_browse_category_id: filters.browse_category_id,
        p_item_types: filters.item_types,
        p_trade_id: filters.trade_id,
        p_classification_category_id: filters.classification_category_id,
        p_include_system: filters.include_system,
        p_page: page,
        p_page_size: PAGE_SIZE,
        p_is_vat_registered: vatQuery.data?.is_vat_registered ?? false,
        p_company_vat_rate: vatQuery.data?.vat_rate ?? 15,
      });
      if (error) throw error;
      return (data as CatalogItem[]) ?? [];
    },
    enabled: !!companyId,
    staleTime: 30 * 1000,
  });

  const items = query.data ?? [];
  const totalCount = items[0]?.total_count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const updateFilter = useCallback(<K extends keyof CatalogFilters>(
    key: K,
    value: CatalogFilters[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setDebouncedSearch('');
    setPage(1);
  }, []);

  return {
    items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    totalCount,
    totalPages,
    page,
    pageSize: PAGE_SIZE,
    filters,
    setPage,
    updateSearch,
    updateFilter,
    resetFilters,
  };
}
